

import { Component, Vue, Watch, Ref } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"

import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"

// @ts-ignore
import draggable from "vuedraggable"

import DataTable from "../DataTable.vue"
import DataForm from "../DataForm.vue"
import FieldSelector from "../Fields/FieldSelector.vue"
import FieldPermissions from "../Permissions/FieldPermissions.vue"
import TemplateActions from "./TemplateActions.vue"
import TemplateRules from "./TemplateRules.vue"
import TemplateCadence from "./TemplateCadence.vue"
import DefinitionTitle from "../Definitions/DefinitionTitle.vue"
import Numeric from "@/components/Fields/Numeric.vue"
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import FieldControl from "@/components/Fields/FieldControl.vue"
import AddSection from "@/components/Config/AddSection.vue"
import TemplateInheritanceOverridePicker from "./TemplateInheritanceOverridePicker.vue"
import TemplateInheritanceChain from "./TemplateInheritanceChain.vue"

import UserImage from "../../UserImage.vue"

import { Template, TemplateDetails, Field, Severity, FieldInstance, CaseAction, CaseRule, Team, DataDefinitionWithFields } from "@/types"
import User, { CurrentUser } from "@/types/user"
import {
	OverrideType,
	FieldOverride,
	SectionOverride,
	InheritanceOverrides
} from "@/types/inheritanceOverrides"

import api from "@/services/api"
import apiSeverity from "@/services/api_Severity"

import { LayoutLocations, LayoutLocationsLabels } from "@/types/templateLayout"
import TeamPermissionsSelector from "@/components/Config/Permissions/TeamPermissionsSelector.vue"
import TemplateScheduleSetup from "./TemplateScheduling.vue"
import cloneDeep from "lodash/cloneDeep"
import { DefinitionSpecialTypes } from "@/types/dataSourceItem"
import { formatFieldsMixin } from "@/mixins/formatFieldsMixin"
import ConfigurationColumn from "@/types/configurationColumn"

import StringValidation from "@/scripts/stringValidation"
import { formatDateMixin } from "@/mixins/formatDateMixin"

import _, { get } from "lodash"
import FolderService from "@/services/folder-service"
import { Folder, FolderItems, FolderType } from "@/types/folder"
import { SortByOptions } from "@/types/schedule"
import { folderMixin } from "@/mixins/folderMixin"
import { TenantFeatures } from "@/types/tenantFeatures"
import { BModal } from "bootstrap-vue"
import SectionedFieldSelector from "../Fields/SectionedFieldSelector.vue"
import { SelectLabeledValue } from "@/types/labeledValue"
import TemplateInheritanceChangingModal from "./TemplateInheritanceChangingModal.vue"
import { InheritedTemplateChangingParams } from "@/types/templateDetails"
import TextDisplayWithCopy from "../Fields/TextDisplayWithCopy.vue"
import TemplateInheritanceChangeSummaryModal from "./TemplateInheritanceChangeSummaryModal.vue"

const setup = namespace("setup")

@Component({
	components: {
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"data-table": DataTable,
		"data-form": DataForm,
		"field-select": FieldSelector,
		"field-permissions": FieldPermissions,
		"user-image": UserImage,
		"template-actions": TemplateActions,
		"template-rules": TemplateRules,
		"template-scheduling": TemplateScheduleSetup,
		"template-cadence": TemplateCadence,
		draggable,
		"skeleton": SkeletonLoader,
		"definition-title": DefinitionTitle,
		"field-control": FieldControl,
		"team-permissions-selector": TeamPermissionsSelector,
		"add-section": AddSection,
		"sectioned-field-selector": SectionedFieldSelector,
		"inheritance-changing-modal": TemplateInheritanceChangingModal,
		"text-display-with-copy": TextDisplayWithCopy,
		"template-inheritance-override-picker": TemplateInheritanceOverridePicker,
		"template-inheritance-chain": TemplateInheritanceChain,
		"inheritance-change-summary-modal": TemplateInheritanceChangeSummaryModal
	}
})
export default class TemplateSetup extends Vue {
	$refs!: {
		dataTable: DataTable,
		templateInheritanceChain: TemplateInheritanceChain,
	}

	@Ref() inheritanceChangingModal!: TemplateInheritanceChangingModal

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;

	@Getter("getMe")
	public me!: CurrentUser;

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Action public refreshUserInfo!: () => void;

	private severities: Severity[] = [];
	private severitiesDict: any = null;

	@setup.Getter("getFieldTypesById")
	fieldTypesById!: { [key: string]: any };

	@setup.Getter("getDataSourcesById")
	dataSourceDictionary!: { [key: string]: any };

	@setup.Getter("getTeams")
	teams!: Team[];

	@setup.State
	fields!: Field[];

	@setup.Action loadFields!: () => Promise<any[]>;

	@Getter("getTenantSettings") getTenantSettings!: { [key: string]: any }

	private layoutLocationsLabels = LayoutLocationsLabels
	private OverrideType = OverrideType
	private FieldOverride = FieldOverride
	private SectionOverride = SectionOverride

	private showInheritanceChangeSummaryModal: boolean = false;
	private inhertitanceChangeRootTemplate: TemplateDetails | null = null;

	public templateDefinition: DataDefinitionWithFields | null = null;

	private cadenceDetailsValid: boolean = false

	private changingInheritedTemplate: InheritedTemplateChangingParams | null = null

	private folderService = new FolderService()

	private affected: { fieldIds?: Set<string>; sectionIds?: Set<string> } = { fieldIds: undefined, sectionIds: undefined } as any

	private showInheritanceOverridesOnly: boolean = true

	folderType = FolderType.Template
	tenantFeatures = TenantFeatures

	isLoadingOnMount: boolean = true
	isLoadingData: boolean = false
	isLoadingMisc: boolean = false
	isLoadingSuppressed: boolean = false

	public get hasSettingsAuthorization (): boolean {
		return this.me && !!this.me.isAdmin
	}

	public get tableRows (): (FolderItems | Template)[] {
		return this.getIsFeatureEnabled(TenantFeatures.TemplateFolders) ? this.folderService.tableRows() : this.templates
	}

	private get isLoading (): boolean {
		return this.isLoadingOnMount || this.isLoadingData || this.isLoadingMisc
	}

	/**
	 * Trigger the expansion/collapse of nodes depending on whether only overrides are to be shown.
	 */
	@Watch("showInheritanceOverridesOnly")
	private async onShowInheritanceOverridesOnlyChanged (): Promise<void> {
		await this.$nextTick()
		this.$refs["templateInheritanceChain"].setDefaultExpandedState()
	}

	private folderDeleted (folder: Folder) {
		const folderTemplate = this.templates.filter(template => template.folder === folder.title || !this.folderService.isParentFolder(folder.title, template.folder))
		folderTemplate.forEach(template => template.folder = null)
		this.folderService.loadFolderItemGroups(this.templates)
	}

	private toggleExpandFolder (folder: any) {
		if (!folder.isFolderType) {
			return
		}
		const folderRow: FolderItems = folder
		folderRow.expanded = !folderRow.expanded
	}

	private rowClass (item: any, type: any): string {
		if (!item || type !== "row") return ""
		if (item.isFolderType) return "folder-row"

		return ""
	}

	public get templateDefinitionFields (): FieldInstance[] {
		if (this.templateDefinition && this.templateDefinition.fields) {
			return this.templateDefinition.fields
		}
		return []
	}

	public async loadTemplateObjectDefinition () {
		this.templateDefinition = await api.specialDefinitionWithFields(DefinitionSpecialTypes.Template)
	}

	private get canDisplayIdColumn (): boolean {
		return this.getIsFeatureEnabled ? this.getIsFeatureEnabled(this.tenantFeatures.AdminShowTemplateId) && (this.me.isAdmin || false) : false
	}

	public get columns (): ConfigurationColumn[] {
		var columns: ConfigurationColumn[] = [
			{
				key: "folderOrExpandable",
				label: "Folder",
				sortable: false,
				inModal: () => false,
				inTable: this.getIsFeatureEnabled ? this.getIsFeatureEnabled(this.tenantFeatures.TemplateFolders) : false,
				hasTemplate: true,
				filterByFormatted: true
			},
			{
				key: "id",
				label: "ID",
				sortable: false,
				hasTemplate: true,
				readonly: true,
				inModal: (item) => false,
				inTable: this.canDisplayIdColumn,
				dynamicComponent: TextDisplayWithCopy,
				componentProperties: {
					displayValue: true
				}
			},
			{
				key: "title",
				label: "Title",
				sortable: true,
				filterable: true,
				errors: (item: any) => StringValidation.validateTitle(item.title)

			},
			{
				key: "description",
				label: "Description",
				sortable: true,
				filterable: true,
				inModal: () => false
			},
			{
				key: "folder",
				label: "Folder",
				dataType: "list",
				options: this.folderService.currentUser ? this.folderService.getPermittedSelectionFolders() : [],
				sortable: true,
				inTable: false,
				inModal: () => this.getIsFeatureEnabled ? this.getIsFeatureEnabled(this.tenantFeatures.TemplateFolders) : false
			},
			{
				key: "createdBy.name",
				label: "Created By",
				inModal: (item: any) => false,
				hasTemplate: true
			},
			{
				key: "lastUpdated",
				label: "Last Updated",
				inModal: (item: any) => false,
				hasTemplate: true,
				filterByFormatted: true,
				formatter: (value: string) => formatDateMixin.methods.displayDateFromISOString(value)
			},
			{
				key: "actions",
				inModal: (item: any) => false,
				inTable: false,
				errors: (item: any) => item.actions && item.actions.some((action: CaseAction) => !action.title)
					? "No title entered for action"
					: item.rules && item.rules.some((r: CaseRule) => !r.id) ? "Please select a rule" : null
			},
			{
				key: "cadenceDetails",
				inModal: (item: any) => false,
				inTable: false,
				errors: (item: any) => !this.cadenceDetailsValid ? "Please enter valid cadence details" : null
			}
		]

		if (this.templateDefinitionFields.length > 0) {
			const fieldColumns: any[] = this.templateDefinitionFields.map(c => {
				return {
					key: "templateDefinitionFields." + c.field.id! + ".value",
					label: c.field.title,
					dataType: c.field.dataType,
					hasTemplate: true,
					dataSourceId: c.field.dataSourceId,
					inTable: c.includeInTable,
					sortable: true,
					field: c.field,
					inModal: (item: any) => false
				}
			})
			columns = columns.concat(fieldColumns)
		}

		return columns
	}

	public get additionalColumns (): ConfigurationColumn[] {
		var additionalColumns: ConfigurationColumn[] = [
			{
				key: "id",
				label: "ID",
				sortable: false,
				hasTemplate: true,
				readonly: true,
				inModal: (item) => item.id && this.canDisplayIdColumn,
				inTable: false,
				dynamicComponent: TextDisplayWithCopy,
				componentProperties: {
					displayValue: true
				}
			},
			{
				key: "description",
				label: "Description",
				sortable: true,
				filterable: true,
				inTable: false
			},
			{
				key: "inheritedFromTitle",
				label: "Inherited From",
				dataType: "list",
				valueColumn: "inheritedFrom",
				sortable: true,
				inTable: false,
				hasDataFormTemplate: true,
				tooltip: "Use this to pull in information from another template. For instance, you may have a general template known as “Facilities Issues” which requires a basic set of information (People involved, Dates of occurrence). From there, you could make an “Elevator Entrapment” Template that inherits from the “Facilities Issue” template. This way, only a handful of additional Fields need to be added, e.g. an elevator identification number."
			},
			{
				key: "allowFromApi",
				label: "Creatable from API",
				dataType: "checkbox",
				valueColumn: "allowFromApi",
				inModal: (item: any) => true,
				inTable: false,
				tooltip: "Enables items of this template to be created via the Cases API. This needs to be enabled in order to raise events from Enterprise or Operations into Cases."
			},
			{
				key: "preventManualCreation",
				label: "Prevent Manual Creation",
				dataType: "checkbox",
				valueColumn: "preventManualCreation",
				inModal: (item: any) => true,
				inTable: false,
				tooltip: `Disables users from being able to manually create ${this.getRecordName === this.getCaseName ? this.getRecordName + "s" : this.getRecordName + "s/" + this.getCaseName + "s"} for this template, however they can still be created automatically via Schedules or Cadence.`
			},
			{
				key: "severityId",
				label: "Default Severity Level",
				dataType: "list",
				valueColumn: "severityId",
				options: this.severities,
				inModal: (item: any) => true,
				readonly: (item: TemplateDetails) => !!item.inheritedFrom,
				inTable: false,
				tooltip: "Initial severity level when records are created"
			},
			{
				key: "caseNumberFormat",
				label: "Case Number Template",
				readonly: (item: TemplateDetails) => !!item.inheritedFrom,
				dynamicComponent: DefinitionTitle,
				componentProperties: {
					label: "",
					description: "Format for case number. Leave blank to use simple case number",
					fields: [
						{
							field: {
								id: "caseNumber",
								title: "Case Number"
							}
						},
						{
							field: {
								id: "month",
								title: "Month"
							}
						},
						{
							field: {
								id: "year",
								title: "Year"
							}
						}
					],
					requiredFields: ["caseNumber"],
					allowEmpty: true
				},
				tooltip: "By setting a case number format on a template, you can customize how records with that template will get case numbers. By default, case numbers will be a simple increasing number (1, 2, 3). With a format, you can mix in text, as well as years and months, as well as having a fixed length on the case number itself (eg, SV-2021-00001)",
				errors: (item: any) => item.caseNumberFormat !== "" && item.caseNumberFormat && item.caseNumberFormat.indexOf("{caseNumber}") === -1
					? "Must include case number"
					: null
			},
			{
				key: "caseNumberFormatLength",
				label: "Case Number Length",
				readonly: (item: TemplateDetails) => !!item.inheritedFrom,
				dynamicComponent: Numeric,
				componentProperties: {
					precision: 0
				},
				tooltip: "Provide a number to specify the length of the case numbers associated with this template."
			},
			{
				key: "hiddenFields",
				label: "Hide Built-in Fields",
				dataType: "list-multiple",
				valueColumn: "hiddenFields",
				options: this.builtInFields,
				readonly: (item: TemplateDetails) => !!item.inheritedFrom,
				inModal: (item: any) => true,
				inTable: false,
				tooltip: "Sometimes it may not be applicable to use all of the built in fields for a case, therefore it is possible to hide these fields from records and cases."
			},
			{
				key: "alwaysPromoted",
				label: "Always Promoted",
				dataType: "checkbox",
				valueColumn: "alwaysPromoted",
				inModal: (item: any): boolean => {
					const tenantSettings = this.getTenantSettings
					const hideAlwaysPromoteOption = !!tenantSettings && tenantSettings["allTemplatesAlwaysPromoted"]
					return !hideAlwaysPromoteOption
				},
				inTable: false,
				tooltip: `Automatically promotes ${this.getRecordName}s to ${this.getCaseName}s when they are created. If checked, this will also promote any ${this.getRecordName}s created with Scheduling or Cadence Scheduling.`
			},
			{
				key: "enableTitleTemplate",
				label: "Title Template",
				dataType: "checkbox",
				valueColumn: "enableTitleTemplate",
				readonly: (item: TemplateDetails) => this.shouldTitleTemplateBeReadOnly,
				inModal: (item: any) => true,
				inTable: false,
				tooltip: "The title template is a setting on the Template Setup view which allows for an operator to pre-define an auto-generated title based on the certain field types that belong to a template. Once set a user will be unable to manually alter the title, and can only do so by editing the selected field values."
			}
		]

		if (this.templateDetails?.enableTitleTemplate) {
			var titleTemplate: ConfigurationColumn = {
				key: "titleTemplate",
				dataType: "",
				readonly: (item: TemplateDetails) => this.shouldTitleTemplateBeReadOnly,
				dynamicComponent: DefinitionTitle,
				componentProperties: {
					label: "",
					description: "",
					definition: this.getFieldsForTitle(this.templateDetails)
				}
			}

			additionalColumns.push(titleTemplate)
		}

		if (this.templateDefinitionFields.length > 0) {
			const fieldColumns: any[] = this.templateDefinitionFields.map(c => {
				return {
					key: "templateDefinitionFields." + c.field.id! + ".value",
					labelKeyPath: "templateDefinitionFields." + c.field.id! + ".label",
					label: c.field.title,
					dataType: c.field.dataType,
					hasTemplate: true,
					dataSourceId: c.field.dataSourceId,
					inTable: false,
					sortable: true,
					field: c.field,
					tooltip: c.field.description,
					inModal: (item: any) => true
				}
			})
			additionalColumns = additionalColumns.concat(fieldColumns)
		}

		return additionalColumns
	}

	public get fieldColumns (): any[] {
		return [
			{
				key: "handle",
				label: ""
			},
			{
				key: "field.title",
				label: "Title"
			},
			{
				key: "field.dataTypeTitle",
				label: "Data Type"
			},
			{
				key: "field.dataSourceTitle",
				label: "Data Source"
			},
			{
				key: "field.allowFiles",
				label: "Allow Files"
			},
			{
				key: "requiredRecord",
				label: "Required on Record"
			},
			{
				key: "permissions.record",
				label: "Record Permissions"
			},
			{
				key: "requiredCase",
				label: "Required on Case"
			},
			{
				key: "permissions.case",
				label: "Case Permissions"
			},
			{
				key: "remove",
				label: ""
			}
		]
	}

	public get builtInFields (): any[] {
		return [
			{
				id: "summary",
				title: "Summary"
			},
			{
				id: "status",
				title: "Status"
			},
			{
				id: "severity",
				title: "Severity"
			},
			{
				id: "affectedAreas",
				title: "Affected Areas"
			},
			{
				id: "links",
				title: "Links"
			},
			{
				id: "tags",
				title: "Tags"
			},
			{
				id: "attachments",
				title: "Attachments"
			}
		]
	}

	public templateDetails: TemplateDetails | null = null;
	public templates: Template[] = [];

	showRemoveFieldMessage: boolean = false;

	public inheritedTemplate: TemplateDetails | null = null
	public inheritedFromLoaded: string | null = null;
	public fieldFilter: string = "";

	public addFieldToSection: string | null = null;

	private fieldsToRemove: FieldInstance[] = [];

	public get inheritFrom () {
		return this.templateDetails ? this.templateDetails.inheritedFrom : null
	}

	public get inheritableTemplates (): Template[] {
		if (!this.templateDetails || !this.templateDetails.id) {
			return this.templates
		}

		const inheritanceChain = new Set<string>()
		inheritanceChain.add(this.templateDetails.id)

		const addInherited = (parent: Template) => {
			this.templates.filter(child => child.inheritedFrom === parent.id).forEach(child => {
				if (child.id) {
					inheritanceChain.add(child.id)
					addInherited(child)
				}
			})
		}

		addInherited(this.templateDetails)

		return this.templates.filter(template => template.id && !inheritanceChain.has(template.id))
	}

	public async updateInherited (value?: string, fieldsToKeep: string[] = []) {
		if (this.templateDetails && (!value || value !== this.inheritedFromLoaded)) {
			if (value) {
				this.inheritedTemplate = this.changingInheritedTemplate?.toTemplate?.id === value
					? this.changingInheritedTemplate?.toTemplate
					: await api.templateDetails(value)
				const inheritedTemplate = cloneDeep(this.inheritedTemplate)

				this.templateDetails.severityId = inheritedTemplate.severityId
				this.templateDetails.severityTitle = inheritedTemplate.severityTitle
				this.templateDetails.hiddenFields = inheritedTemplate.hiddenFields
				this.templateDetails.caseNumberFormat = inheritedTemplate.caseNumberFormat
				this.templateDetails.caseNumberFormatLength = inheritedTemplate.caseNumberFormatLength
				if (inheritedTemplate.enableTitleTemplate) {
					this.templateDetails.enableTitleTemplate = true
					this.templateDetails.titleTemplate = inheritedTemplate.titleTemplate
				}

				if (!this.templateDetails.permissions) {
					this.templateDetails.permissions = inheritedTemplate.permissions
				} else if (inheritedTemplate.permissions) {
					if (inheritedTemplate.permissions!.record) {
						if (!this.templateDetails.permissions!.record) {
							this.templateDetails.permissions!.record = {
								view: [],
								edit: []
							}
						}
						this.templateDetails.permissions!.record.view = this.addInheritedTeams(
							inheritedTemplate.permissions!.record.view,
							this.templateDetails.permissions!.record.view
						)
						this.templateDetails.permissions!.record.edit = this.addInheritedTeams(
							inheritedTemplate.permissions!.record.edit,
							this.templateDetails.permissions!.record.edit
						)
					}
					if (inheritedTemplate.permissions!.case) {
						if (!this.templateDetails.permissions!.case) {
							this.templateDetails.permissions!.case = {
								view: [],
								edit: []
							}
						}
						this.templateDetails.permissions!.case.view = this.addInheritedTeams(
							inheritedTemplate.permissions!.case.view,
							this.templateDetails.permissions!.case.view
						)
						this.templateDetails.permissions!.case.edit = this.addInheritedTeams(
							inheritedTemplate.permissions!.case.edit,
							this.templateDetails.permissions!.case.edit
						)
					}
				}

				if (inheritedTemplate.editClosedPermissions) {
					if (!this.templateDetails.editClosedPermissions) {
						this.templateDetails.editClosedPermissions = inheritedTemplate.editClosedPermissions
					} else {
						this.templateDetails.editClosedPermissions = [
							...inheritedTemplate.editClosedPermissions.filter(teamId => !this.templateDetails!.editClosedPermissions!.includes(teamId)),
							...this.templateDetails.editClosedPermissions
						]
					}
				}

				const sectionsToKeep = new Set<string>()

				if (fieldsToKeep.length > 0) {
					this.templateDetails.fields.forEach((field: FieldInstance) => {
						if (fieldsToKeep.includes(field.field.id!)) {
							field.inheritedFrom = undefined
							field.inheritedFromTitle = undefined
							sectionsToKeep.add(field.section)
						}
					})
				}

				this.templateDetails.fields.removeWhere((field: FieldInstance) =>
					field.inheritedFrom != null && !inheritedTemplate.fields.some(
						(inheritedField: FieldInstance) => inheritedField.field.id === field.field.id))

				inheritedTemplate.fields.forEach((field: FieldInstance) => {
					const existingField = this.templateDetails!.fields.find((existingField: FieldInstance) => existingField.field.id === field.field.id)
					if (!existingField) {
						if (!field.inheritedFrom) {
							field.inheritedFrom = inheritedTemplate.id!
							field.inheritedFromTitle = inheritedTemplate.title!
						}

						this.templateDetails!.fields.push(field)
					} else {
						existingField.inheritedFrom = inheritedTemplate.id!
						existingField.inheritedFromTitle = inheritedTemplate.title!
					}
				})

				this.templateDetails.actions.removeWhere((action: CaseAction) =>
					action.inheritedFrom != null && !inheritedTemplate.actions.some(
						(inheritedAction: CaseAction) => inheritedAction.id === action.id))

				inheritedTemplate.actions.forEach((action: CaseAction) => {
					if (!this.templateDetails!.actions.some((existingAction: CaseAction) => existingAction.id === action.id)) {
						if (!action.inheritedFrom) {
							action.inheritedFrom = inheritedTemplate.id!
							action.inheritedFromTitle = inheritedTemplate.title!
						}

						this.templateDetails!.actions.push(action)
					}
				})

				this.templateDetails.rules.removeWhere((rule: CaseRule) =>
					rule.inheritedFrom != null && !inheritedTemplate.rules.some(
						(inheritedRule: CaseRule) => inheritedRule.id === rule.id))

				inheritedTemplate.rules.forEach((rule: CaseRule) => {
					if (!this.templateDetails!.rules.some((existingRule: CaseRule) => existingRule.id === rule.id)) {
						if (!rule.inheritedFrom) {
							rule.inheritedFrom = inheritedTemplate.id!
							rule.inheritedFromTitle = inheritedTemplate.title!
						}

						this.templateDetails!.rules.push(rule)
					}
				})

				this.templateDetails.sections.removeWhere(section => {
					if (sectionsToKeep.has(section)) {
						this.templateDetails!.sectionDetails[section].inheritedFrom = undefined
						this.templateDetails!.sectionDetails[section].inheritedFromTitle = undefined
						return false
					}

					if (this.templateDetails!.sectionDetails[section].inheritedFrom != null &&
						!inheritedTemplate.sections.some(inheritedSection => inheritedSection === section)) {
						delete this.templateDetails!.sectionDetails[section]
						return true
					}
					return false
				})

				inheritedTemplate.sections.forEach((section) => {
					if (!this.templateDetails!.sections.some((existingSection) => existingSection === section)) {
						var sectionDetail = inheritedTemplate!.sectionDetails[section]
						if (sectionDetail && !sectionDetail.inheritedFrom) {
							sectionDetail.inheritedFrom = inheritedTemplate.id!
							sectionDetail.inheritedFromTitle = inheritedTemplate.title!
						}

						this.templateDetails!.sections.push(section)
						this.templateDetails!.sectionDetails[section] = { ...sectionDetail, editOverrides: false } as any
					}
				})

				if (this.templateDetails.inheritedDefaults && this.templateDetails.fieldDefaultValues) {
					Object.keys(this.templateDetails.inheritedDefaults).forEach(fieldId => {
						Vue.delete(this.templateDetails!.fieldDefaultValues as any, fieldId)
					})
				}

				Vue.set(this.templateDetails, "inheritedDefaults", inheritedTemplate.fieldDefaultValues)
				if (inheritedTemplate.fieldDefaultValues) {
					if (!this.templateDetails.fieldDefaultValues) {
						Vue.set(this.templateDetails, "fieldDefaultValues", {})
					}

					Object.keys(inheritedTemplate.fieldDefaultValues).forEach(fieldId => {
						Vue.set(this.templateDetails!.fieldDefaultValues as any, fieldId, inheritedTemplate.fieldDefaultValues![fieldId])
					})
				}
			} else {
				this.inheritedTemplate = null

				if (this.templateDetails.inheritedDefaults) {
					this.templateDetails.inheritedDefaults = null
				}

				const sectionsToKeep = new Set<string>()

				if (fieldsToKeep.length > 0) {
					this.templateDetails.fields.forEach((field: FieldInstance) => {
						if (fieldsToKeep.includes(field.field.id!)) {
							field.inheritedFrom = undefined
							field.inheritedFromTitle = undefined
							sectionsToKeep.add(field.section)
						}
					})
				}

				this.templateDetails.fields.removeWhere((field: FieldInstance) => field.inheritedFrom != null)
				this.templateDetails.actions.removeWhere((action: CaseAction) => action.inheritedFrom != null)
				this.templateDetails.rules.removeWhere((rule: CaseRule) => rule.inheritedFrom != null)
				this.templateDetails.sections.removeWhere(section => {
					if (sectionsToKeep.has(section)) {
						this.templateDetails!.sectionDetails[section].inheritedFrom = undefined
						this.templateDetails!.sectionDetails[section].inheritedFromTitle = undefined
						return false
					}

					if (this.templateDetails!.sectionDetails[section].inheritedFrom != null) {
						delete this.templateDetails!.sectionDetails[section]
						return true
					}
					return false
				})
			}

			this.inheritedFromLoaded = value ?? null
			this.setTemplateDetails(this.templateDetails)
		}
	}

	public async mounted () {
		this.isLoadingOnMount = true
		await this.loadFields()
		await this.folderService.load(this.me, FolderType.Template)
		this.loadData(false)
		this.severities = await apiSeverity.severityList()
		this.severitiesDict = this.severities.toDictionary("id")
		this.loadTemplateObjectDefinition()
		this.isLoadingOnMount = false
	}

	async loadData (setLoadingState = true): Promise<void> {
		if (setLoadingState) this.isLoadingData = true
		this.templates = await api.categories()
		await this.folderService.loadFolders()
		this.folderService.loadFolderItemGroups(this.templates)
		if (setLoadingState) this.isLoadingData = false
		this.isLoadingSuppressed = false
	}

	async loadTemplateDetails (item: Template): Promise<TemplateDetails> {
		let template = this.setTemplateDetails(await api.templateDetails(item!.id!))
		if (template.inheritedFrom) {
			await this.updateInherited(template.inheritedFrom)
		}

		return this.templateDetails ?? template
	}

	setTemplateDetails (templateDetails: TemplateDetails) {
		this.fields.forEach((field: Field) => {
			Vue.set(field, "added", false)
		})

		if (templateDetails.inherited) {
			templateDetails.inherited.forEach((inherited: TemplateDetails) => {
				inherited.fields.forEach((inheritedField: FieldInstance) => {
					inheritedField.inheritedFrom = inherited.title
					inheritedField._rowVariant = "muted"

					const templateField = templateDetails.fields.firstOrDefault(templateField =>
						inheritedField.field.id === templateField.field.id)

					if (templateField) {
						templateField.inheritedFrom = inherited.title
						templateField._rowVariant = "muted"
					} else {
						templateDetails.fields.push(inheritedField)
					}
				})

				inherited.sections.forEach(section => {
					var sectionDetail = inherited!.sectionDetails[section]
					if (sectionDetail && !sectionDetail.inheritedFrom) {
						sectionDetail.inheritedFrom = inherited.id!
						sectionDetail.inheritedFromTitle = inherited.title!
					}

					// Clone the section detail to ensure Vue reactivity works properly
					Vue.set(templateDetails!.sectionDetails, section, {
						...sectionDetail,
						editOverrides: false
					})
				})
			})
		} if (!templateDetails.sections) {
			templateDetails.sections = []
		}

		if (!templateDetails.sectionDetails) {
			templateDetails.sectionDetails = templateDetails.sections.reduce((group, sectionTitle) =>
				({
					...group,
					[sectionTitle]: {
						title: sectionTitle,
						layout: LayoutLocations.Default
					}
				}), {})
		}

		if (!templateDetails.templateSchedule) {
			templateDetails.templateSchedule = {
				schedulingAvailable: false,
				schedulingPermissions: [],
				schedulingFields: [],
				multipleRecordFields: [],
				defaultValues: {}
			}
		}

		if (!templateDetails.cadenceDetails) {
			templateDetails.cadenceDetails = {
				cadencePeriod: null,
				cadenceFields: null,
				isCase: false,
				disablePermissions: [],
				disablePermissionsTeams: []
			}
		}
		templateDetails.sectionFields = templateDetails.fields.reduce((group: any, field: FieldInstance) => {
			var section = field.section ? field.section : "General"

			Vue.set(field, "setValue", false)
			// Only set editOverrides for inherited fields
			if (field.inheritedFrom) {
				Vue.set(field, "editOverrides", false)
			}

			field.field.dataTypeTitle = this.fieldTypesById[field.field.dataType!] ? this.fieldTypesById[field.field.dataType!].title : ""

			if (field.field.dataSourceId && this.dataSourceDictionary[field.field.dataSourceId]) {
				field.field.dataSourceTitle = this.dataSourceDictionary[field.field.dataSourceId].title
			}

			const fieldDefinition = this.fields.find(f => f.id === field.field.id)
			if (fieldDefinition) {
				Vue.set(fieldDefinition, "added", true)
			}

			if (templateDetails.sections.indexOf(section) === -1) {
				templateDetails.sections.push(section)
			}

			if (!templateDetails.sectionDetails[section]) {
				templateDetails.sectionDetails[section] = {
					title: section,
					description: null,
					layout: LayoutLocations.Default,
					permissions: { record: { view: [], edit: [] }, case: { view: [], edit: [] } }
				}
			} else if (!templateDetails.sectionDetails[section].permissions) {
				templateDetails.sectionDetails[section].permissions = { record: { view: [], edit: [] }, case: { view: [], edit: [] } }
			}

			// Only set editOverrides for inherited sections
			if (templateDetails.sectionDetails[section].inheritedFrom) {
				Vue.set(templateDetails.sectionDetails[section], "editOverrides", false)
			}

			(group[section] = group[section] || []).push(field)
			return group
		}, {})

		if (templateDetails.sections) {
			// Remove empty sections
			templateDetails.sections.removeWhere(s => !templateDetails.sectionFields[s] || (templateDetails.sectionFields[s] && templateDetails.sectionFields[s].length === 0))
		}

		for (const section in templateDetails.sectionFields) {
			templateDetails.sectionFields[section].sortBy("order")
		}

		templateDetails.actions.sortBy("order")
		templateDetails.rules.sortBy("order")
		return this.templateDetails = templateDetails
	}

	createNewItem () {
		this.fields.forEach((field: Field) => {
			Vue.set(field, "added", false)
		})

		return this.templateDetails = {
			id: null,
			title: "",
			description: "",
			inheritedFrom: null,
			sections: [],
			sectionDetails: {},
			sectionFields: {},
			fields: [],
			actions: [],
			rules: [],
			permissions: {
				case: {
					view: [],
					edit: []
				},
				record: {
					view: [],
					edit: []
				}
			},
			templateSchedule: {
				schedulingAvailable: false,
				schedulingPermissions: [],
				schedulingFields: [],
				multipleRecordFields: [],
				defaultValues: {}
			},
			cadenceDetails: {
				cadencePeriod: null,
				cadenceFields: null,
				isCase: false,
				disablePermissions: [],
				disablePermissionsTeams: []
			},
			editClosedPermissions: null
		}
	}

	handleSave (template: TemplateDetails) {
		if (this.templateInheritanceOverrideFeatureEnabled) {
			if (this.affected.fieldIds || this.affected.sectionIds) {
				this.inhertitanceChangeRootTemplate = template
				this.showInheritanceChangeSummaryModal = true
			} else {
				this.save(template)
				this.affected = { fieldIds: undefined, sectionIds: undefined }
				this.$refs.dataTable.closeModal()
			}
		} else {
			this.save(template)
		}
	}

	async save (template: TemplateDetails) {
		if (template.severityId) {
			template.severityTitle = this.severitiesDict[template.severityId].title
		}

		if (template.caseNumberFormat === "") {
			template.caseNumberFormat = null
		}

		for (const section in template.sectionFields) {
			template.sectionFields[section].forEach((field: FieldInstance, index: number) => {
				field.section = section
				field.order = index
			})
		}

		if (template.sections) {
			// Remove empty sections
			template.sections.removeWhere(s => !template.sectionFields[s] || (template.sectionFields[s] && template.sectionFields[s].length === 0))
		}

		if (template.actions) {
			template.actions.forEach((action: CaseAction, index: number) => {
				action.order = index
			})
		}

		if (template.rules) {
			template.rules.forEach((rule: CaseRule, index: number) => {
				rule.order = index
			})
		}

		// Add assigned teams to teams property
		var cadenceDetails = template.cadenceDetails
		if (cadenceDetails) {
			cadenceDetails.disablePermissions = cadenceDetails.disablePermissionsTeams ? cadenceDetails.disablePermissionsTeams.map((team: any) => {
				if (team.id) {
					return {
						id: team.id,
						title: team.title
					}
				} else {
					const teamDetails = this.teams.find(f => f.id === team)
					if (teamDetails) {
						return {
							id: teamDetails.id,
							title: teamDetails.title
						}
					} else {
						return null
					}
				}
			}).filter((team: any) => team) : []
		}

		template.inherited = null
		await api.templateSave(template)
		this.loadData()
		this.refreshUserInfo()
	}

	private handleEditCancelled () {
		this.affected = { fieldIds: undefined, sectionIds: undefined }
	}

	async deleteItem (template: TemplateDetails) {
		await api.templateDelete(template)
		this.loadData()
	}

	async addFields (addFields: Field[]) {
		if (!this.templateDetails) {
			return
		}

		if (!this.addFieldToSection) {
			return
		}

		addFields.forEach(field => {
			field.added = true

			if (!this.templateDetails!.fields.some((caseField: FieldInstance) => caseField.field.id === field.id)) {
				const newCaseField = {
					field,
					section: this.addFieldToSection,
					order: 0,
					requiredRecord: false,
					requiredCase: false,
					permissions: {
						case: null,
						record: null
					},
					value: null,
					fromData: null,
					includeInTable: false
				} as FieldInstance

				Vue.set(newCaseField, "setValue", false)

				this.templateDetails!.fields.push(newCaseField)
				if (this.templateDetails!.sections.indexOf(this.addFieldToSection!) === -1) {
					this.templateDetails!.sections.push(this.addFieldToSection!)
				}

				if (!this.templateDetails!.sectionFields[this.addFieldToSection!]) {
					Vue.set(this.templateDetails!.sectionFields, this.addFieldToSection!, [])
				}

				this.templateDetails!.sectionFields[this.addFieldToSection!].push(newCaseField)
			}
		})

		this.addFieldToSection = null
	}

	async removeField (fieldToRemove: FieldInstance) {
		if (!this.templateDetails) {
			return
		}

		this.fieldsToRemove = []
		this.fieldsToRemove.push(fieldToRemove)

		// Search if the field is required by any computed Fields
		this.templateDetails!.fields.filter(f => f.field.extensions && f.field.extensions!.fields).forEach(computedField => {
			if (computedField.field.extensions!.fields.find((id: string) => id === fieldToRemove.field.id)) {
				this.fieldsToRemove.push(computedField)
			}
		})

		// Show remove field warning if multiple fields will be removed
		if (this.fieldsToRemove.length >= 2) {
			this.showRemoveFieldMessage = true
		} else {
			this.removeFields()
		}
	}

	async removeFields () {
		this.fieldsToRemove.forEach(field => {
			const fieldDefinition = this.fields.find(f => f.id === field.field.id)
			if (fieldDefinition) {
				fieldDefinition.added = false
			}

			this.templateDetails!.fields.removeWhere(f => f.field.id === field.field.id)
			this.templateDetails!.sectionFields[field.section!].removeWhere((f: FieldInstance) => f.field.id === field.field.id)
		})

		this.showRemoveFieldMessage = false
		this.fieldsToRemove = []
	}

	async addNewField (fieldId: string) {
		await this.loadFields()
		this.setTemplateDetails(this.templateDetails!)

		const newField = this.fields.firstOrDefault((f: Field) => f.id === fieldId)
		this.addFields([newField])
	}

	createSection (newSectionName: string) {
		if (newSectionName) {
			if (!this.templateDetails!.sectionFields[newSectionName!]) {
				Vue.set(this.templateDetails!.sectionFields, newSectionName!, [])
				this.templateDetails!.sections.push(newSectionName)
			}

			if (!this.templateDetails!.sectionDetails[newSectionName]) {
				Vue.set(this.templateDetails!.sectionDetails, newSectionName, {
					title: newSectionName,
					layout: LayoutLocations.Default,
					permissions: { record: { view: [], edit: [] }, case: { view: [], edit: [] } }
				})
			}
		}
	}

	addAction () {
		if (this.templateDetails) {
			this.templateDetails.actions.push({
				title: "",
				requiredCase: false,
				requiredRecord: false,
				order: 0
			})
		}
	}

	addRule () {
		if (this.templateDetails) {
			this.templateDetails.rules.push({
				id: null,
				title: null,
				runOnCase: false,
				runOnRecord: false,
				order: 0
			})
		}
	}

	public ruleSelected () {
	}

	sectionNameChange (oldValue: string, newValue: string) {
		const changedFields: FieldInstance[] = []

		this.templateDetails!.sectionFields[oldValue].forEach((f: FieldInstance) => {
			if (!f.inheritedFrom) {
				f.section = newValue
				changedFields.push(f)
			}
		})

		if (!this.templateDetails!.sectionFields[newValue]) {
			Vue.set(this.templateDetails!.sectionFields, newValue, [])
		}

		this.templateDetails!.sectionFields[newValue] =
			this.templateDetails!.sectionFields[newValue].concat(changedFields)

		this.templateDetails!.sectionFields[oldValue] = this.templateDetails!.sectionFields[oldValue]
			.filter((f: FieldInstance) => f.section === oldValue)

		if (this.templateDetails!.sections.indexOf(newValue) === -1) {
			this.templateDetails!.sections.push(newValue)
		}

		if (this.templateDetails!.sectionFields[oldValue].length === 0) {
			delete this.templateDetails!.sectionFields[oldValue]
			this.templateDetails!.sections.removeWhere(s => s === oldValue)
		}

		if (!this.templateDetails!.sectionDetails[newValue]) {
			Vue.set(this.templateDetails!.sectionDetails, newValue, this.templateDetails!.sectionDetails[oldValue])
			delete this.templateDetails!.sectionDetails[oldValue]
		}
	}

	public get (item: any, columnKey: string): any {
		const dotIndex = columnKey.indexOf(".")
		if (dotIndex > -1) {
			const topLevel = columnKey.substring(0, dotIndex)
			const rest = columnKey.substring(dotIndex + 1)
			return this.get(item[topLevel], rest)
		}

		return item[columnKey]
	}

	get sectionDragOptions () {
		return {
			animation: 200,
			group: "sections",
			disabled: false,
			ghostClass: "ghost"
		}
	}
	get fieldDragOptions () {
		return {
			animation: 200,
			group: "fields",
			disabled: false,
			ghostClass: "ghost"
		}
	}
	public getFieldsForTitle (item: any) {
		const allowedFieldType = ["text", "number", "dollar", "date", "date-time", "list", "list-multiple"]
		if (item.fields) {
			let filteredFields = item.fields.filter((fields: any) => allowedFieldType.includes(fields.field.dataType))
			return {
				fields: filteredFields
			}
		} else {
			return {
				fields: null
			}
		}
	}

	private defaultValue (fieldId: string) {
		if (!this.templateDetails || !this.templateDetails.fieldDefaultValues) {
			return null
		}

		const defaultValue = this.templateDetails.fieldDefaultValues[fieldId]
		return defaultValue === undefined ? null : defaultValue
	}

	private defaultValueIsSet (fieldId: string): boolean {
		if (!this.templateDetails || !this.templateDetails.fieldDefaultValues) {
			return false
		}
		return this.templateDetails.fieldDefaultValues[fieldId] !== undefined
	}

	private defaultValueInherited (fieldId: string): boolean {
		return !!this.templateDetails &&
			!!this.templateDetails.inheritedDefaults &&
			this.templateDetails.inheritedDefaults[fieldId] !== undefined
	}

	private defaultValueSet (fieldId: string, value: any) {
		if (!this.templateDetails) {
			return null
		}

		if (!this.templateDetails.fieldDefaultValues) {
			if (value === undefined) {
				return
			}

			Vue.set(this.templateDetails, "fieldDefaultValues", {})
		}

		if (value === undefined) {
			Vue.delete(this.templateDetails.fieldDefaultValues as any, fieldId)
		} else {
			Vue.set(this.templateDetails.fieldDefaultValues as any, fieldId, value)
		}
	}

	private sectionPermissionsSet (section: string): boolean {
		if (!section || !this.templateDetails!.sectionDetails[section]) {
			return false
		}

		var sectionDetail = this.templateDetails!.sectionDetails[section]

		var casePermissionsSet = !!(sectionDetail.permissions.case.view.length > 0 || sectionDetail.permissions.case.edit.length > 0)
		var recordPermissionsSet = !!(sectionDetail.permissions.record.view.length > 0 || sectionDetail.permissions.record.edit.length > 0)
		return casePermissionsSet || recordPermissionsSet
	}

	private sectionPermissionChange (section: string) {
		if (!section || !this.templateDetails!.sectionDetails[section]) {
			return false
		}

		// Reset field permissions when section permissions are unset
		if (!this.sectionPermissionsSet(section)) {
			this.templateDetails!.sectionFields[section].forEach(f => {
				f.permissions.case = null
				f.permissions.record = null
			})
		}
	}

	private addInheritedTeams (baseTeams: Team[], childTeams: Team[] | null): Team[] {
		if (!childTeams) return baseTeams

		return [
			...baseTeams.filter(t =>
				!childTeams.some(vt => vt.id === t.id)),
			...childTeams || []
		]
	}

	public templateFieldSlotName (templateField: FieldInstance): string {
		return "column.templateDefinitionFields." + templateField.field.id! + ".value"
	}

	public getDisplayLabel (dataItem: any, templateField: FieldInstance) {
		if (!dataItem.templateDefinitionFields || !dataItem.templateDefinitionFields[templateField.field.id!]) {
			return ""
		}

		const labeledValue = dataItem.templateDefinitionFields[templateField.field.id!]
		const dataType = templateField.field.dataType!

		return formatFieldsMixin.methods.getDisplayLabelFromLabeledValue(labeledValue, dataType)
	}

	private get helpText () {
		return `Templates are used as a framework for your ${this.getRecordName}s and ${this.getCaseName}s. ` +
			"They define what types of information should be captured to describe a particular incident. " +
			"Templates consist of a set of Fields, Permissions, Actions, and Rules."
	}

	private get shouldTitleTemplateBeReadOnly (): boolean {
		let item = this.templateDetails
		if (!item || !item.inheritedFrom || item.inheritedFrom === "") {
			return false
		}

		// loop to search up the inheritance chain for compatibility with older data.
		// looping is not needed for more newly created data - values are set on all inheriting templates, so can just check parent
		let parent: Template | null =
			this.templates.firstOrDefault(template => template.id === item!.inheritedFrom)
		while (parent) {
			if (parent.enableTitleTemplate) {
				return true
			} else if (!parent.inheritedFrom) {
				break
			}
			parent = this.templates.firstOrDefault(template => template.id === parent!.inheritedFrom)
		}

		return false
	}

	private get inheritableTemplateOptions (): SelectLabeledValue<string | null>[] {
		return this.inheritableTemplates.map(template => {
			return {
				value: template.id,
				label: template.title
			}
		})
	}

	private async inheritedTemplateChanging (oldValue: SelectLabeledValue<string> | null,
		newValue: SelectLabeledValue<string> | null, setValue: ((value: any) => null)
	) {
		const inheritedTemplateChangeOptionsFeatureEnabled =
			this.getIsFeatureEnabled(TenantFeatures.InheritedTemplateChangeOptions)

		if (!inheritedTemplateChangeOptionsFeatureEnabled || !oldValue?.value) {
			setValue(newValue)
			this.updateInherited(newValue?.value)
			return
		}

		const toTemplate = newValue?.value ? await api.templateDetails(newValue?.value) : null

		const hasExistingInheritedFields =
			this.templateDetails!.fields.some(field => !!field.inheritedFrom)

		this.changingInheritedTemplate = {
			from: oldValue ?? { label: null, value: null },
			to: newValue ?? { label: null, value: null },
			toTemplate,
			losingFields: (!toTemplate && hasExistingInheritedFields) || (!!toTemplate &&
				this.templateDetails!.fields.some(field =>
					!!field.inheritedFrom &&
					!toTemplate.fields.some(toField => toField.field.id === field.field.id))
			),
			hasOverlap: !!toTemplate && this.templateDetails!.fields.some(field =>
				toTemplate.fields.some(toField => toField.field.id === field.field.id)
			),
			setValue: setValue
		}

		await this.$nextTick()
		this.inheritanceChangingModal.show()
	}

	private inheritedTemplateChangeConfirmed (argument: string = "") {
		if (!this.changingInheritedTemplate) {
			return
		}

		let fieldsToKeep: string[]

		switch (argument) {
			case "keepAll":
				fieldsToKeep = this.templateDetails!.fields.map(field => field.field.id!)
				break
			case "removeAll":
				fieldsToKeep = []
				break
			default:
				fieldsToKeep = this.inheritanceChangingModal.selectedFields
		}

		this.inheritedFromLoaded = null
		this.updateInherited(this.changingInheritedTemplate.to.value ?? undefined, fieldsToKeep)
		this.changingInheritedTemplate.setValue(this.changingInheritedTemplate.to)
		this.changingInheritedTemplate = null
	}

	private inheritedTemplateChangeCancelled () {
		this.changingInheritedTemplate = null
	}

	/* --- Inheritance Overrides --- */

	private get templateInheritanceOverrideFeatureEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.TemplateInheritanceOverride)
	}

	private get templateInheritanceOverrides (): InheritanceOverrides | null {
		return this.templateDetails?.inheritanceOverrides ?? null
	}

	private hasInheritanceOverride (id: string, overrideType: OverrideType, overrides?: FieldOverride | SectionOverride): boolean {
		switch (overrideType) {
			case OverrideType.Field:
				const fieldOverrides = this.templateInheritanceOverrides?.fields?.[id]
				if (!fieldOverrides) return false
				if (overrides !== undefined) {
					return fieldOverrides.includes(overrides as FieldOverride)
				}
				// If no specific override is passed, check if any are set
				return fieldOverrides.length > 0
			case OverrideType.Section:
				const sectionOverrides = this.templateInheritanceOverrides?.sections?.[id]
				if (!sectionOverrides) return false
				if (overrides !== undefined) {
					return sectionOverrides.includes(overrides as SectionOverride)
				}
				// If no specific override is passed, check if any are set
				return sectionOverrides.length > 0
			default:
				return false
		}
	}

	private getOverrides (id: string, overrideType: OverrideType): FieldOverride[] | SectionOverride[] {
		switch (overrideType) {
			case OverrideType.Field:
				return this.templateDetails?.inheritanceOverrides?.fields?.[id] || []
			case OverrideType.Section:
				return this.templateDetails?.inheritanceOverrides?.sections?.[id] || []
			default:
				return []
		}
	}

	private setOverrides (id: string, overrideType: OverrideType, overrides: FieldOverride[] | SectionOverride[]): void {
		if (!this.templateDetails) return
		if (!this.templateDetails.inheritanceOverrides) {
			Vue.set(this.templateDetails, "inheritanceOverrides", { fields: {}, sections: {} })
		}

		switch (overrideType) {
			case OverrideType.Field:
				Vue.set(this.templateDetails.inheritanceOverrides!.fields, id, overrides as FieldOverride[])
				break
			case OverrideType.Section:
				Vue.set(this.templateDetails.inheritanceOverrides!.sections, id, overrides as SectionOverride[])
				break
		}
	}

	private handleFieldOverrideInput (field: FieldInstance, overrides: FieldOverride[]): void {
		this.setOverrides(field.field.id!, OverrideType.Field, overrides)
		if (field.field.id) {
			if (!this.affected?.fieldIds) {
				this.affected!.fieldIds = new Set<string>()
			}
			this.affected.fieldIds = new Set(this.affected.fieldIds.add(field.field.id))
		}
	}

	private handleSectionOverrideInput (sectionKey: string, overrides: SectionOverride[]): void {
		this.setOverrides(sectionKey, OverrideType.Section, overrides)
		if (sectionKey) {
			if (!this.affected?.sectionIds) {
				this.affected!.sectionIds = new Set<string>()
			}
			this.affected.sectionIds = new Set(this.affected.sectionIds.add(sectionKey))
		}
	}

	private handleOverrideClear (overrideType: OverrideType, id: string): void {
		switch (overrideType) {
			case OverrideType.Field:
				this.setOverrides(id, OverrideType.Field, [])
				this.handleOverrideBack(OverrideType.Field, id)
				break
			case OverrideType.Section:
				this.setOverrides(id, OverrideType.Section, [])
				this.handleOverrideBack(OverrideType.Section, id)
				break
		}
	}

	private handleOverrideBack (overrideType: OverrideType, id: string): void {
		switch (overrideType) {
			case OverrideType.Field:
				const field = this.templateDetails?.fields.find(f => f.field.id === id)
				if (field) {
					Vue.set(field, "editOverrides", false)
				}
				break
			case OverrideType.Section:
				if (this.templateDetails && this.templateDetails.sectionDetails[id]) {
					Vue.set(this.templateDetails.sectionDetails[id], "editOverrides", false)
				}
				break
		}

		// Force re-render for the overrides component to pick up the change
		this.$forceUpdate()
	}

	private handleInheritanceChangeSummaryModalConfirm (template: TemplateDetails) {
		Vue.set(this, "showInheritanceChangeSummaryModal", false)
		this.save(template)
		this.$refs.dataTable.closeModal()
		this.inhertitanceChangeRootTemplate = null
		this.affected = { fieldIds: undefined, sectionIds: undefined }
	}

	private handleInheritanceChangeSummaryModalCancel () {
		this.showInheritanceChangeSummaryModal = false
	}

	private toggleSectionEditOverrides (section: string) {
		if (!this.templateInheritanceOverrideFeatureEnabled || !this.templateDetails || !this.templateDetails.sectionDetails[section]) {
			return
		}
		Vue.set(this.templateDetails.sectionDetails[section], "editOverrides", true)

		// Force re-render for the overrides component to pick up the change
		this.$forceUpdate()
	}

	private toggleFieldEditOverrides (field: FieldInstance) {
		if (!this.templateInheritanceOverrideFeatureEnabled || !field) {
			return
		}
		Vue.set(field, "editOverrides", true)
	}

	private onRowClicked (item: FolderItems | Template) {
		if ((item as FolderItems).isFolderType) {
			this.toggleExpandFolder(item)
			return
		}

		this.resetItemViewProperties()
	}

	/**
	 * Resets any global properties ready to edit an item.
	 */
	private resetItemViewProperties () {
		// Display inheritance overrides only
		this.showInheritanceOverridesOnly = true
	}

	private expandInheritanceChain (): void {
		this.$refs["templateInheritanceChain"].expandAll()
	}

	private collapseInheritanceChain (): void {
		this.$refs["templateInheritanceChain"].collapseAll(true)
	}
}
